import { fabric } from "fabric";
import { CropMask, ModuleInfoImage } from "../../Types";
import customControls from "./customControlsHelper";

export default {
  handleImageOnly_ImageChange,
  imageMounter_OnlyImage,
  handleGridResize,
  objectGridPositioner,
  imageGridCoordinates,
  handleLinesForImageGrid,
  addColorMasks,
};

/**
 * @param images
 * @param canvas
 * @param extraProps {fabric.IObjectOptions}
 * @param skippedIndex {number[]}
 * @param preservedObjects {string[]}
 * @param fillImages
 * @description Method used to handle changes for image-only module.
 * Up to 9 images are allowed for the user to add to the canvas.
 * These images size is not editable.
 */
export function handleImageOnly_ImageChange(
  images: ModuleInfoImage[],
  canvas: fabric.Canvas,
  withMask: boolean,
  cellOffset = 14,
  enableFilters: boolean,
  extraProps: fabric.IObjectOptions = {},
  skippedIndex: number[] = [],
  preservedObjects: string[] = [],
  disableControls = true
) {
  // skip deletion of some types of object
  if (preservedObjects && preservedObjects.length) {
    canvas.forEachObject((object) => {
      preservedObjects.map((type) => {
        if (object.type !== type) {
          canvas.remove(object);
        }
      });
    });
  } else {
    canvas.remove(...canvas.getObjects());
  }
  let skipCounts = 0;
  let skipped: boolean = false;
  for (let i = 0; i < images.length; i++) {
    skippedIndex.map((index) => {
      if (index === i) {
        skipCounts++;
        skipped = true;
      }
    });
    if (images[i].renderUrl) {
      imageMounter_OnlyImage(
        canvas,
        images[i].renderUrl,
        images[i].id,
        i + skipCounts,
        null,
        true,
        extraProps,
        skipped,
        !!preservedObjects && !!preservedObjects.length,
        withMask,
        disableControls,
        cellOffset,
        enableFilters
      );
    }
    skipped = false;
  }
}

function addColorMasks(
  canvas: fabric.Canvas,
  cellOffset: number,
  index: number
) {
  canvas.getObjects("image").forEach((object) => {
    canvas.sendToBack(object);
  });

  let filters = [
    "#fbebeb",
    "#2e61bc",
    "#ea4534",
    "#f1d00f",
    "#c08d8d",
    "#dc7ebb",
    "#04a44c",
    "#d474b3",
    "#87339b",
  ];
  let cellWidth = canvas.getWidth() / 3;
  let cellHeight = canvas.getHeight() / 3;

  let obj = new fabric.Rect({
    // @ts-ignore
    id: `color-filter-${index}`,
    width: cellWidth,
    height: cellHeight,
    fill: filters[index],
    opacity: 0.3,
    // ...fixedProps,
    // ...extraProps,
  });

  const { left, top } = imageGridCoordinates(index, cellWidth, cellHeight);
  objectGridPositioner(obj, cellWidth, cellHeight, left, top, cellOffset);
  // const { left, top } = imageGridCoordinates(index, cellWidth, cellHeight);
  canvas.add(obj);
  canvas.bringToFront(obj);
}

/**
 * @method imageMounter_OnlyImage
 * @param canvas {fabricCanvasInstance} Fabric instance that is to be edited
 * @param url {string} Image's url
 * @param imageId {string | number} Image's identifier
 * @param index {number} Image's index
 * @param cropBox {CropMask} Cropper properties containing information for the
 * crop box, such as image's original size, crop size etc... necessary to
 * calculate different coefficients.
 * @description Method used to mount an image base on its index. This method
 * is both used if there is a cropMask or not, meaning that it will make the
 * image auto-scale.
 */
function imageMounter_OnlyImage(
  canvas: fabric.Canvas,
  url: string,
  imageId: string | number,
  index: number,
  cropBox: CropMask | null,
  fixed,
  extraProps: fabric.IObjectOptions,
  skipped: boolean,
  preservedObjects: boolean,
  withMask: boolean,
  disableControls = true,
  cellOffset = 14,
  enableFilters = false
) {
  let cellWidth = canvas.getWidth() / 3;
  let cellHeight = canvas.getHeight() / 3;
  let fixedProps = {};
  if (fixed) {
    fixedProps = {
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: "pointer",
    };
  }

  if (skipped) {
    let obj = new fabric.Rect({
      // @ts-ignore
      id: "kids-background-4",
      width: cellWidth,
      height: cellHeight,
      fill: "white",
      ...fixedProps,
      ...extraProps,
    });
    // position the object in canvas
    const { left, top } = imageGridCoordinates(
      index - 1,
      cellWidth,
      cellHeight
    );
    objectGridPositioner(obj, cellWidth, cellHeight, left, top, cellOffset);
    canvas.add(obj);
    canvas.discardActiveObject();
    if (preservedObjects) obj.sendToBack();
  }
  const { left, top } = imageGridCoordinates(index, cellWidth, cellHeight);
  if (withMask) {
    mountImageWithMask(
      canvas,
      url,
      left,
      top,
      fixedProps,
      extraProps,
      cellWidth,
      cellHeight,
      preservedObjects,
      index,
      cropBox,
      disableControls,
      cellOffset,
      enableFilters
    );
  } else {
    mountImageWithoutMask(
      canvas,
      url,
      left,
      top,
      fixedProps,
      extraProps,
      cellWidth,
      cellHeight,
      preservedObjects,
      index,
      disableControls,
      cellOffset,
      enableFilters
    );
  }
}

function mountImageWithMask(
  canvas,
  url: string,
  left: number,
  top: number,
  fixedProps,
  extraProps,
  cellWidth,
  cellHeight,
  preservedObjects,
  index: number,
  cropBox: CropMask,
  disableControls = true,
  cellOffset = 14,
  enableFilters
) {
  fabric.Image.fromURL(
    url,
    (img) => {
      // img.set({ crossOrigin: "anonymous" });
      img.scaleToWidth(cellWidth / window.devicePixelRatio);
      let imgScaledWidth = img.getScaledWidth();
      let imgScaledHeight = img.getScaledHeight();

      // TODO: Need more work
      if (imgScaledHeight <= cellHeight) {
        img.scaleToHeight(cellHeight / window.devicePixelRatio);
      }
      let maskPosition: any = {};
      let patternSize: any = {};
      if (cropBox) {
        // resize coefficient of the image respective to the its screen size
        let holderCoef = cellWidth / cropBox.originalWidth;
        // zoom coefficient
        let croppedAreaZoomCoef = cropBox.originalWidth / cropBox.width;
        // multiplication of holderCoef & croppedAreaZoomCoef for simplification
        let transformCoef = croppedAreaZoomCoef * holderCoef;
        // Watch <ImageCropper/>, image is again resize while displayed in cropper
        let maskCoef = cropBox.originalHeight / cropBox.containerHeight;
        // IMPORTANT: image is divided by 2, this is a library bug.
        img.scaleToWidth(
          (cropBox.originalWidth * transformCoef) / window.devicePixelRatio
        );
        maskPosition = {
          offsetX: -(cropBox.left * transformCoef) * maskCoef,
          offsetY: -(cropBox.top * transformCoef) * maskCoef,
        };
        patternSize = {
          width: cropBox.originalWidth * transformCoef,
          height: cropBox.originalHeight * transformCoef,
        };
      } else {
        maskPosition = {
          offsetX: (cellWidth - imgScaledWidth * window.devicePixelRatio) / 2,
          offsetY: (cellHeight - imgScaledHeight * window.devicePixelRatio) / 2,
        };
        patternSize = {
          width: imgScaledWidth,
          height: imgScaledHeight,
        };
      }
      // make a temporary canvas with the image
      let patternSourceCanvas = new fabric.StaticCanvas("c");
      patternSourceCanvas.add(img);
      patternSourceCanvas.renderAll();
      // make a fabric canvas to use the temporary canvas as background
      let pattern = new fabric.Pattern({
        // @ts-ignore
        source: function() {
          patternSourceCanvas.setDimensions({
            // use pattern size
            ...patternSize,
          });
          patternSourceCanvas.renderAll();
          return patternSourceCanvas.getElement();
        },
        repeat: "no-repeat",
        // use mask position
        ...maskPosition,
      });

      // make the object with common props belonging to all
      let obj = new fabric.Rect({
        // @ts-ignore
        id: `image-${index}`,
        customType: "image",
        width: cellWidth,
        height: cellHeight,
        fill: pattern,
        ...fixedProps,
        ...extraProps,
        objectCaching: false,
      });
      // position the object in canvas
      objectGridPositioner(
        obj,
        cellWidth - cellOffset,
        cellHeight - cellOffset,
        left + cellOffset / 2,
        top + cellOffset / 2,
        cellOffset
      );
      // @ts-ignore
      obj.customiseCornerIcons(customControls());
      if (disableControls) {
        obj.setControlsVisibility({
          mtr: false,
          ml: false,
          mr: false,
          mb: false,
          mt: false,
          bl: false,
          br: false,
          tl: false,
          tr: false,
        });
      }
      canvas.add(obj);
      canvas.discardActiveObject();
    },
    { crossOrigin: "anonymous" }
  );
}

function mountImageWithoutMask(
  canvas,
  url: string,
  left: number,
  top: number,
  fixedProps,
  extraProps,
  cellWidth,
  cellHeight,
  preservedObjects,
  index: number,
  disableControls = true,
  cellOffset = 14,
  enableFilters
) {
  fabric.Image.fromURL(url, (img) => {
    img.set({
      // @ts-ignore
      id: `image-${index}`,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      // selectable: false,
      hoverCursor: "pointer",
      left: left,
      top: top,
      ...fixedProps,
      ...extraProps,
    });
    objectGridPositioner(
      img,
      cellWidth - cellOffset,
      cellHeight - cellOffset,
      left + cellOffset / 2,
      top + cellOffset / 2,
      cellOffset
    );
    if (disableControls) {
      img.setControlsVisibility({
        mtr: false,
        ml: false,
        mr: false,
        mb: false,
        mt: false,
        bl: false,
        br: false,
        tl: false,
        tr: false,
      });
    }
    if (enableFilters) {
      let filters = [
        "#fbebeb",
        "#2e61bc",
        "#ea4534",
        "#f1d00f",
        "#c08d8d",
        "#dc7ebb",
        "#04a44c",
        "#d474b3",
        "#87339b",
      ];

      let obj = new fabric.Rect({
        // @ts-ignore
        id: `color-filter-${index}`,
        left: left,
        top: top,
        fill: filters[index],
        opacity: 0.3,
        ...fixedProps,
        // ...extraProps,
      });
      objectGridPositioner(
        obj,
        cellWidth - cellOffset,
        cellHeight - cellOffset,
        left + cellOffset / 2,
        top + cellOffset / 2,
        cellOffset
      );
      // @ts-ignore
      obj.customiseCornerIcons(customControls());

      if (disableControls) {
        obj.setControlsVisibility({
          mtr: false,
          ml: false,
          mr: false,
          mb: false,
          mt: false,
          bl: false,
          br: false,
          tl: false,
          tr: false,
        });
      }
      canvas.add(obj);
      canvas.bringToFront(obj);
    }
    // @ts-ignore
    img.customiseCornerIcons(customControls());
    canvas.add(img);
    canvas.discardActiveObject();
    if (preservedObjects || enableFilters) img.sendToBack();
  });
}

/**
 * @method handleGridResize
 * @param canvas {fabricCanvasInstance} Fabric instance that is to be edited
 * @returns void
 * @description Method used to recalculate on window resize event,
 * top and left for modules where photos are positioned as a grid 3x3.
 */
function handleGridResize(canvas: fabric.Canvas, offset = 0): void {
  let cellWidth = canvas.getWidth() / 3;
  let cellHeight = canvas.getHeight() / 3;
  let object = canvas.getObjects();
  for (let i = 0; i < object.length; i++) {
    const obj = object[i];
    const { top, left } = imageGridCoordinates(
      // @ts-ignore
      obj.id.charAt(obj.id.length - 1),
      cellWidth,
      cellHeight
    );
    objectGridPositioner(
      obj,
      cellWidth - offset,
      cellHeight - offset,
      left + offset / 2,
      top + offset / 2,
      offset
    );
  }
}

/**
 * @method objectGridPositioner
 * @param image {fabric.Object}
 * @param cellWidth {number} Frame width
 * @param cellHeight {number} Frame height
 * @param leftBoundary {number} Left boundary
 * @param topBoundary {number} Top boundary
 */
function objectGridPositioner(
  obj: fabric.Object,
  cellWidth,
  cellHeight,
  leftBoundary,
  topBoundary,
  cellOffset
): void {
  if (obj.type === "rect") {
    obj.set({
      top: topBoundary,
      left: leftBoundary,
      width: cellWidth,
      height: cellHeight,
    });
  } else if (obj.type == "textbox") {
    // specific to kids module
    obj.set({
      left: cellWidth * 1.5,
      top: cellHeight * 1.5,
      width: cellWidth,
      height: cellHeight,
    });
  } else {
    if (cellOffset) {
      obj.set({
        top: topBoundary,
        left: leftBoundary,
      });
      if (obj.width > obj.height) {
        obj.set({
          width: obj.height * (cellWidth / cellHeight),
          height: obj.height,
          scaleX: cellHeight / obj.height,
          scaleY: cellHeight / obj.height,
        });
      } else {
        obj.set({
          width: obj.width,
          height: obj.width * (cellHeight / cellWidth),
          scaleX: cellWidth / obj.width,
          scaleY: cellWidth / obj.width,
        });
      }
    } else {
      // offset objects depending on their size
      if (obj.width > cellWidth) {
        // resize
        obj.scaleToWidth(cellWidth);
        // watch for height fit
        if (obj.getScaledHeight() > cellHeight) {
          // console.log(obj, cellWidth, obj.getScaledWidth(), leftBoundary);
          // resize again
          obj.scaleToHeight(cellHeight);
          // reposition
          obj.set({
            top: topBoundary,
            left: (cellWidth - obj.getScaledWidth()) / 2 + leftBoundary,
          });
        } else {
          // reposition
          obj.set({
            top: (cellHeight - obj.getScaledHeight()) / 2 + topBoundary,
            left: leftBoundary,
          });
        }
      } else if (obj.height > cellHeight) {
        // resize
        obj.scaleToHeight(cellHeight);
        if (obj.getScaledWidth() > cellWidth) {
          // rescale again
          obj.scaleToWidth(cellWidth);
          // reposition
          obj.set({
            top: (cellHeight - obj.getScaledHeight()) / 2 + topBoundary,
            left: leftBoundary,
          });
        } else {
          obj.set({
            top: topBoundary,
            left: (cellWidth - obj.getScaledWidth()) / 2 + leftBoundary,
          });
        }
      }
    }
  }
}

/**
 * @method imageGridCoordinates
 * @param index {number} Image position in array, initially images are loaded in state
 * saved in an array
 * @param cellWidth {number} Frame width
 * @param cellHeight {number} Frame height
 * @description Helper function to calculate top and left
 * coordinates to position images in a grid 3x3.
 */
function imageGridCoordinates(
  index,
  cellWidth,
  cellHeight
): { top: number; left: number } {
  let left: number;
  let top: number;
  if (index == 0) {
    top = 0;
    left = 0;
  }
  if (index == 1) {
    top = 0;
    left = cellWidth;
  }
  if (index == 2) {
    top = 0;
    left = cellWidth * 2;
  }
  if (index == 3) {
    top = cellHeight;
    left = 0;
  }
  if (index == 4) {
    top = cellHeight;
    left = cellWidth;
  }
  if (index == 5) {
    top = cellHeight;
    left = cellWidth * 2;
  }
  if (index == 6) {
    top = cellHeight * 2;
    left = 0;
  }
  if (index == 7) {
    top = cellHeight * 2;
    left = cellWidth;
  }
  if (index == 8) {
    top = cellHeight * 2;
    left = cellWidth * 2;
  }
  return {
    top: top,
    left: left,
  };
}

function handleLinesForImageGrid(
  canvas: fabric.Canvas,
  lineWidth: boolean | number
) {
  if (lineWidth && typeof lineWidth == "number") {
    let canvasWidth = canvas.getWidth();
    let canvasHeight = canvas.getHeight();
    let cellWidth = canvasWidth / 3;
    let cellHeight = canvasHeight / 3;

    for (let i = 0; i < 4; i++) {
      let strokeLeft = cellWidth * i;
      if (strokeLeft != 0) {
        strokeLeft = cellWidth * i - lineWidth / 2;
      }

      if (i == 3) {
        strokeLeft = cellWidth * i - lineWidth / 2;
      }

      canvas.add(
        new fabric.Line([lineWidth, canvas.getHeight(), lineWidth, 0], {
          left: strokeLeft,
          top: 0,
          stroke: "white",
          strokeWidth: i == 0 || i == 3 ? lineWidth / 2 : lineWidth,
        })
      );
    }

    for (let i = 0; i < 4; i++) {
      let strokeTop = cellHeight * i;
      if (strokeTop != 0) {
        strokeTop = cellHeight * i - lineWidth / 2;
      }

      if (i == 3) {
        strokeTop = cellHeight * i - lineWidth / 2;
      }

      canvas.add(
        new fabric.Line([0, lineWidth, canvas.getHeight(), lineWidth], {
          left: 0,
          top: strokeTop,
          stroke: "white",
          strokeWidth: i == 0 || i == 3 ? lineWidth / 2 : lineWidth,
        })
      );
    }
  } else {
    window.canvasRef.getObjects("line").forEach((line) => {
      window.canvasRef.remove(line);
    });
  }
}
